
import React, { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { Save } from "lucide-react";

interface SDGsEditorProps {
  onSave: (data: any) => void;
}

interface SDGsFormData {
  title_en: string;
  title_ar: string;
  subtitle_en: string;
  subtitle_ar: string;
  cta_en: string;
  cta_ar: string;
}

const SDGsEditor: React.FC<SDGsEditorProps> = ({ onSave }) => {
  const { language } = useLanguage();
  const [isSaving, setIsSaving] = useState(false);
  
  const form = useForm<SDGsFormData>({
    defaultValues: {
      title_en: "Sustainable Development Goals in Palestine",
      title_ar: "أهداف التنمية المستدامة في فلسطين",
      subtitle_en: "Explore how each of the 17 UN Sustainable Development Goals is implemented in Palestine's unique context",
      subtitle_ar: "اكتشف كيف يتم تنفيذ كل من أهداف التنمية المستدامة السبعة عشر للأمم المتحدة في السياق الفريد لفلسطين",
      cta_en: "Discover how this goal is applied in Palestinian context",
      cta_ar: "اكتشف كيف يتم تطبيق هذا الهدف في السياق الفلسطيني"
    },
  });
  
  const handleSubmit = (data: SDGsFormData) => {
    setIsSaving(true);
    
    // Simulate API call to save data
    setTimeout(() => {
      setIsSaving(false);
      onSave(data);
    }, 1000);
  };
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {language === "en" ? "SDGs Section Content" : "محتوى قسم أهداف التنمية المستدامة"}
            </CardTitle>
            <CardDescription>
              {language === "en" 
                ? "Edit the main content of the SDGs section" 
                : "تحرير المحتوى الرئيسي لقسم أهداف التنمية المستدامة"
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="title_en"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {language === "en" ? "Section Title (English)" : "عنوان القسم (الإنجليزية)"}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="subtitle_en"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {language === "en" ? "Section Description (English)" : "وصف القسم (الإنجليزية)"}
                      </FormLabel>
                      <FormControl>
                        <Textarea {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="cta_en"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {language === "en" ? "Button Text (English)" : "نص الزر (الإنجليزية)"}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="title_ar"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {language === "en" ? "Section Title (Arabic)" : "عنوان القسم (العربية)"}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} dir="rtl" />
                      </FormControl>
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="subtitle_ar"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {language === "en" ? "Section Description (Arabic)" : "وصف القسم (العربية)"}
                      </FormLabel>
                      <FormControl>
                        <Textarea {...field} dir="rtl" />
                      </FormControl>
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="cta_ar"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {language === "en" ? "Button Text (Arabic)" : "نص الزر (العربية)"}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} dir="rtl" />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <div className="flex justify-end">
          <Button 
            type="submit" 
            className="bg-palsustain-green hover:bg-palsustain-green/90"
            disabled={isSaving}
          >
            <Save className="mr-2 h-4 w-4" />
            {isSaving 
              ? (language === "en" ? "Saving..." : "جاري الحفظ...") 
              : (language === "en" ? "Save Changes" : "حفظ التغييرات")
            }
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default SDGsEditor;
